import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    createDevice,
    getAllDeviceTypes,
    getHouseAllDevices,
    searchDeviceModels,
} from '../../api_calls/api_calls_devices';

const AddDevicesModal = ({ open, onClose, houseId, dictionary, onDevicesUpdated }) => {
    const SEARCH_DEBOUNCE_MS = 350;
    const [loadingModels, setLoadingModels] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [addingId, setAddingId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [models, setModels] = useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [selectedDeviceType, setSelectedDeviceType] = useState('');
    const [houseDevices, setHouseDevices] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const hasLoggedInitialModelsRef = useRef(false);

    const virtualDevices = useMemo(
        () => houseDevices.filter((d) => d?.is_virtual === true),
        [houseDevices],
    );
    const visibleDeviceTypes = useMemo(
        () =>
            deviceTypes.filter((t) => {
                const name = (t?.name || '').trim().toUpperCase();
                return name !== 'EM' && name !== '3EM';
            }),
        [deviceTypes],
    );
    const effectiveSearchQuery = useMemo(
        () => [selectedDeviceType, searchQuery.trim()].filter(Boolean).join(' ').trim(),
        [selectedDeviceType, searchQuery],
    );
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    const displayModels = useMemo(() => {
        if (!selectedDeviceType) return models;
        return models.filter((m) => String(m?.device_type || '') === String(selectedDeviceType));
    }, [models, selectedDeviceType]);

    const loadHouseDevices = () => {
        if (!houseId) return Promise.resolve();
        setLoadingDevices(true);
        return getHouseAllDevices(houseId)
            .then((list) => setHouseDevices(Array.isArray(list) ? list : []))
            .catch(() => setHouseDevices([]))
            .finally(() => setLoadingDevices(false));
    };

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        setError('');
        setSuccess('');
        setSearchQuery('');
        setSelectedDeviceType('');
        hasLoggedInitialModelsRef.current = false;
        Promise.allSettled([getHouseAllDevices(houseId), getAllDeviceTypes()])
            .then(([devicesRes, typesRes]) => {
                if (!mounted) return;
                setModels([]);
                setDeviceTypes(typesRes.status === 'fulfilled' ? typesRes.value : []);
                if (!hasLoggedInitialModelsRef.current && typesRes.status === 'fulfilled') {
                    const typeList = Array.isArray(typesRes.value) ? typesRes.value : [];
                    hasLoggedInitialModelsRef.current = true;
                }
                setHouseDevices(devicesRes.status === 'fulfilled' ? devicesRes.value : []);
            });
        return () => {
            mounted = false;
        };
    }, [open, houseId]);

    useEffect(() => {
        if (!open) return;
        const timerId = setTimeout(() => {
            setDebouncedSearchQuery(effectiveSearchQuery);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timerId);
    }, [effectiveSearchQuery, open]);

    useEffect(() => {
        if (!open) return;
        const q = debouncedSearchQuery;
        if (!q) {
            setModels([]);
            return;
        }
        setLoadingModels(true);
        searchDeviceModels(q)
            .then((list) => {
                const resultList = Array.isArray(list) ? list : [];
                setModels(resultList);
                if (!hasLoggedInitialModelsRef.current) {
                    const categories = Array.from(
                        new Set(
                            resultList
                                .map((m) => (m?.device_type || '').trim())
                                .filter(Boolean),
                        ),
                    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                    hasLoggedInitialModelsRef.current = true;
                }
            })
            .catch(() => setModels([]))
            .finally(() => setLoadingModels(false));
    }, [debouncedSearchQuery, open]);

    const handleAddDevice = async (model) => {
        if (!houseId || !model?.id) return;
        const modelName = model?.name || '';
        const modelType = model?.device_type || '';
        const modelBrand = model?.brand || '';
        const inferredName = `${modelBrand || modelType || 'Device'} ${modelName}`.trim();
        const payload = {
            name: inferredName,
            device_id: '',
            is_em: false,
            relay: 0,
            device_types_id: model.id,
            houses_id: houseId,
            is_virtual: true,
            device_model: modelName,
        };
        setAddingId(model.id);
        setError('');
        try {
            await createDevice(payload);
            setSuccess(dictionary.addDevicesAdded || 'Device added');
            await loadHouseDevices();
            if (typeof onDevicesUpdated === 'function') onDevicesUpdated();
        } catch (_) {
            setError(dictionary.addDevicesAddError || 'Failed to add device');
        } finally {
            setAddingId('');
        }
    };

    return (
        <>
            <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Paper sx={{ width: '100%', maxWidth: 860, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
                    <Box sx={{ px: 2.5, py: 2, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar sx={{ width: 38, height: 38, bgcolor: '#60a5fa' }}>
                                    <DevicesOtherOutlinedIcon sx={{ color: '#fff' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        {dictionary.addDevicesTitle || 'Add Devices'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {dictionary.addDevicesSubtitle || 'Search and add virtual devices for this apartment'}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        <TextField
                            fullWidth
                            placeholder={dictionary.addDevicesSearchPlaceholder || 'Search by brand, type or model...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <SearchIcon sx={{ color: 'text.secondary' }} />
                                            {selectedDeviceType ? (
                                                <Chip
                                                    size="small"
                                                    color="primary"
                                                    label={selectedDeviceType}
                                                    onDelete={() => setSelectedDeviceType('')}
                                                />
                                            ) : null}
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f8fafc' } }}
                        />
                        {visibleDeviceTypes.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5 }}>
                                <Chip
                                    size="small"
                                    label={dictionary.addDevicesAllTypes || 'All types'}
                                    onClick={() => {
                                        setSelectedDeviceType('');
                                    }}
                                    color={selectedDeviceType === '' ? 'primary' : 'default'}
                                    variant={selectedDeviceType === '' ? 'filled' : 'outlined'}
                                    sx={{ cursor: 'pointer' }}
                                />
                                {visibleDeviceTypes.map((typeObj) => {
                                    const typeName = String(typeObj?.name || '');
                                    const active = selectedDeviceType === typeName;
                                    return (
                                        <Chip
                                            key={typeObj?.id || typeName}
                                            size="small"
                                            label={typeName}
                                            onClick={() => {
                                                if (active) {
                                                    setSelectedDeviceType('');
                                                    return;
                                                }
                                                setSelectedDeviceType(typeName);
                                            }}
                                            color={active ? 'primary' : 'default'}
                                            variant={active ? 'filled' : 'outlined'}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    );
                                })}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {effectiveSearchQuery === '' ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    {dictionary.addDevicesStartSearch || 'Start typing to search devices'}
                                </Typography>
                            ) : loadingModels ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress />
                                </Box>
                            ) : displayModels.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    {dictionary.addDevicesNoResults || 'No matching device models found'}
                                </Typography>
                            ) : (
                                displayModels.map((model, index) => (
                                    <Box
                                        key={`${model?.id || 'model'}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: 3,
                                            bgcolor: '#fff',
                                            border: '1px solid #e5e7eb',
                                            '&:hover': { borderColor: '#93c5fd', boxShadow: '0 4px 10px rgba(148,163,184,0.25)' },
                                        }}
                                    >
                                        <Avatar sx={{ width: 46, height: 46, bgcolor: '#e0f2fe', border: '2px solid #38bdf8', color: '#0284c7' }}>
                                            <DevicesOtherOutlinedIcon />
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                                {model?.name || '-'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.4, flexWrap: 'wrap' }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ bgcolor: '#f1f5f9', px: 1, py: 0.25, borderRadius: 99, fontWeight: 700 }}
                                                >
                                                    {model?.brand || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {model?.device_type || '-'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            onClick={() => handleAddDevice(model)}
                                            disabled={addingId === model?.id}
                                            sx={{ color: '#0284c7' }}
                                        >
                                            {addingId === model?.id ? <CircularProgress size={20} /> : <AddCircleOutlineIcon />}
                                        </IconButton>
                                    </Box>
                                ))
                            )}
                        </Box>

                        <Box sx={{ mt: 2.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                {dictionary.addDevicesCurrentVirtual || 'Current virtual devices'}
                            </Typography>
                            {loadingDevices ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : virtualDevices.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    {dictionary.addDevicesNoVirtual || 'No virtual devices added yet'}
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {virtualDevices.map((d, i) => (
                                        <Box
                                            key={`${d?.id || d?.device_id || 'dev'}-${i}`}
                                            sx={{
                                                p: 1.2,
                                                border: '1px solid #e2e8f0',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {d?.name || d?.device_model || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {d?.device_type_name || d?.device_model || '-'}
                                                </Typography>
                                            </Box>
                                            <DeleteOutlineIcon sx={{ color: '#ef4444', opacity: 0.6 }} />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" onClick={onClose}>
                                {dictionary.addDevicesDone || 'Done'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Modal>

            <Snackbar open={Boolean(success)} autoHideDuration={2000} onClose={() => setSuccess('')}>
                <Alert severity="success" variant="filled" onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddDevicesModal;
